import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Plus } from "lucide-react";
import { toast } from "sonner";

// Native ERP port of the standalone Sales Reporter app's "Expenses" tab.
// Mirrors the actual api/add_expense.php: a daily travel + boarding +
// lodging + other breakdown, not a single categorized amount. No update/
// delete route (the original app never exposed one either).
//
// travel_amount is always computed SERVER-SIDE from travel_mode + km (car/
// bike multiply km by a per-km rate from settings; public trusts the
// amount typed in directly; none zeroes both) — the client never sends a
// trusted travel amount for car/bike, it only supplies the inputs the
// calculation needs.
const TRAVEL_MODES = [
  { value: "car", label: "Car" },
  { value: "bike", label: "Bike" },
  { value: "public", label: "Public Transport" },
  { value: "none", label: "None" },
];

const today = () => new Date().toISOString().slice(0, 10);

const empty = {
  expense_date: today(),
  travel_mode: "none",
  km: "",
  travel_amount: "",
  from_location: "",
  to_location: "",
  boarding_amount: "",
  lodging_amount: "",
  other_amount: "",
  notes: "",
  latitude: "",
  longitude: "",
};

export default function SalesExpensesPage() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => setList((await api.get("/sales-report/expenses")).data);
  useEffect(() => { load(); }, []);

  const startCreate = () => { setForm(empty); setOpen(true); };

  const save = async () => {
    const boarding = Math.max(0, Number(form.boarding_amount || 0));
    const lodging = Math.max(0, Number(form.lodging_amount || 0));
    const other = Math.max(0, Number(form.other_amount || 0));
    const km = Math.max(0, Number(form.km || 0));
    const travelAmount = Math.max(0, Number(form.travel_amount || 0));
    // A rough client-side pre-check so an obviously-empty form doesn't
    // round-trip to the server just to be told the same thing; the server
    // still computes and enforces the real total (it knows the per-km
    // rates, the client doesn't).
    const roughlyEmpty = boarding + lodging + other <= 0
      && !(form.travel_mode === "car" || form.travel_mode === "bike" ? km > 0 : travelAmount > 0);
    if (form.travel_mode === "none" && roughlyEmpty) {
      return toast.error("Please enter at least one expense amount or KM travelled");
    }
    const payload = {
      ...form,
      km,
      travel_amount: travelAmount,
      boarding_amount: boarding,
      lodging_amount: lodging,
      other_amount: other,
      latitude: form.latitude === "" ? null : Number(form.latitude),
      longitude: form.longitude === "" ? null : Number(form.longitude),
    };
    setBusy(true);
    try {
      await api.post("/sales-report/expenses", payload);
      toast.success("Expense logged");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const grandTotal = list.reduce((s, e) => s + Number(e.total_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="sales-expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales Report</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Expenses
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Travel, boarding, lodging & other field expenses. Total logged: <span className="mono font-semibold text-slate-700">₹ {grandTotal.toLocaleString()}</span>
          </p>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={startCreate} data-testid="new-expense-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Expense
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader><SheetTitle data-testid="expense-sheet-title">New Expense</SheetTitle></SheetHeader>

            <div className="space-y-5 mt-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} data-testid="expense-date-input" />
                </div>
                <div>
                  <Label>Travel Mode</Label>
                  <Select value={form.travel_mode} onValueChange={(v) => setForm({ ...form, travel_mode: v })}>
                    <SelectTrigger data-testid="expense-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRAVEL_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(form.travel_mode === "car" || form.travel_mode === "bike") && (
                <div>
                  <Label>Distance (km)</Label>
                  <Input type="number" min="0" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} data-testid="expense-km-input" />
                  <p className="text-xs text-slate-500 mt-1">Travel amount is calculated automatically from distance × the {form.travel_mode} rate.</p>
                </div>
              )}
              {form.travel_mode === "public" && (
                <div>
                  <Label>Travel Amount (₹)</Label>
                  <Input type="number" min="0" value={form.travel_amount} onChange={(e) => setForm({ ...form, travel_amount: e.target.value })} data-testid="expense-travel-amount-input" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>From</Label>
                  <Input value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} data-testid="expense-from-input" />
                </div>
                <div>
                  <Label>To</Label>
                  <Input value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} data-testid="expense-to-input" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Boarding (₹)</Label>
                  <Input type="number" min="0" value={form.boarding_amount} onChange={(e) => setForm({ ...form, boarding_amount: e.target.value })} data-testid="expense-boarding-input" />
                </div>
                <div>
                  <Label>Lodging (₹)</Label>
                  <Input type="number" min="0" value={form.lodging_amount} onChange={(e) => setForm({ ...form, lodging_amount: e.target.value })} data-testid="expense-lodging-input" />
                </div>
                <div>
                  <Label>Other (₹)</Label>
                  <Input type="number" min="0" value={form.other_amount} onChange={(e) => setForm({ ...form, other_amount: e.target.value })} data-testid="expense-other-input" />
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="expense-notes-input" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.12em] text-slate-500">Location (optional)</Label>
                <div className="grid grid-cols-2 gap-3 mt-1">
                  <Input type="number" step="any" placeholder="Latitude" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} data-testid="expense-lat-input" />
                  <Input type="number" step="any" placeholder="Longitude" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} data-testid="expense-lng-input" />
                </div>
              </div>
            </div>

            <SheetFooter className="mt-6 gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={busy} data-testid="save-expense-btn">Save</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="expenses-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
              <th className="py-3 px-4">Date</th>
              <th className="py-3 px-4">Mode</th>
              <th className="py-3 px-4">KM</th>
              <th className="py-3 px-4">Travel</th>
              <th className="py-3 px-4">Boarding</th>
              <th className="py-3 px-4">Lodging</th>
              <th className="py-3 px-4">Other</th>
              <th className="py-3 px-4">Total</th>
              <th className="py-3 px-4">Notes</th>
            </tr>
          </thead>
          <tbody>
            {list.map((e) => (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`expense-row-${e.id}`}>
                <td className="py-3 px-4 text-slate-600">{e.expense_date}</td>
                <td className="py-3 px-4 capitalize text-slate-800">{e.travel_mode}</td>
                <td className="py-3 px-4 mono">{Number(e.km || 0)}</td>
                <td className="py-3 px-4 mono">₹ {Number(e.travel_amount || 0).toLocaleString()}</td>
                <td className="py-3 px-4 mono">₹ {Number(e.boarding_amount || 0).toLocaleString()}</td>
                <td className="py-3 px-4 mono">₹ {Number(e.lodging_amount || 0).toLocaleString()}</td>
                <td className="py-3 px-4 mono">₹ {Number(e.other_amount || 0).toLocaleString()}</td>
                <td className="py-3 px-4 mono font-semibold">₹ {Number(e.total_amount || 0).toLocaleString()}</td>
                <td className="py-3 px-4 text-slate-600 text-xs">{e.notes || "—"}</td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={9} className="py-10 text-center text-slate-500 text-sm">No expenses logged yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
